import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Circle, Download, Award, FileText, FileSpreadsheet, FileArchive, Presentation, Loader2 } from "lucide-react";
import { FORMATIONS } from "../data/content";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MOD_ICON = { pdf: FileText, doc: FileText, docx: FileText, txt: FileText, ppt: Presentation, pptx: Presentation, xls: FileSpreadsheet, xlsx: FileSpreadsheet, csv: FileSpreadsheet, zip: FileArchive };

export default function LearnPlayer() {
  const { lookup } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const formation = FORMATIONS.find((f) => f.lookup === lookup);
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, p, c] = await Promise.all([
        axios.get(`${API}/modules`, { params: { formation: lookup } }),
        axios.get(`${API}/lms/progress`),
        axios.get(`${API}/me/certificates`),
      ]);
      setModules(m.data || []);
      const mine = (p.data || []).find((x) => x.formation_lookup === lookup);
      if (!mine) { toast.error("Vous n'avez pas accès à cette formation."); nav("/mon-espace"); return; }
      setProgress(mine);
      setCerts((c.data || []).filter((x) => x.formation_lookup === lookup && x.status === "VALID"));
    } catch (e) {
      if (e?.response?.status === 401) nav("/connexion", { state: { from: `/formation/${lookup}/apprendre` } });
    } finally { setLoading(false); }
  }, [lookup]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const downloadModule = async (m) => {
    try {
      const res = await axios.get(`${API}/modules/${m.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${m.title}.${m.file_ext || "bin"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); }
  };

  const complete = async (m) => {
    setMarking(m.id);
    try {
      const { data } = await axios.post(`${API}/lms/progress/${lookup}/complete-module/${m.id}`);
      setProgress(data);
      if (data.percent >= 100) toast.success("Formation terminée à 100 % — le QCM final est débloqué !");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible."); }
    finally { setMarking(null); }
  };

  if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center"><Loader2 size={30} className="animate-spin text-gold" /></div>;
  if (!formation || !progress) return null;

  const cert = certs[0];

  return (
    <>
      <section className="bg-ink-900 text-paper grain relative pt-32 pb-16 md:pt-40 overflow-hidden">
        <div className="absolute -right-24 top-0 w-[500px] h-[500px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative z-10 mx-auto max-w-[1100px] px-5 md:px-10">
          <Link to="/mon-espace" className="inline-flex items-center gap-2 label-mono text-paper/50 hover:text-gold transition-colors"><ArrowLeft size={14} /> Mon espace</Link>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl md:text-6xl font-semibold mt-6 leading-[0.95]" data-testid="learn-title">
            {formation.title}
          </motion.h1>
          <p className="label-mono text-steel mt-4">{formation.level} · {formation.duration} · {progress.total_modules} modules</p>
          <div className="mt-8 max-w-xl">
            <div className="flex justify-between label-mono text-paper/50 mb-2">
              <span>Progression</span><span data-testid="learn-progress-label">{progress.percent} %</span>
            </div>
            <div className="h-2 rounded-full bg-paper/10 overflow-hidden">
              <motion.div className="h-full bg-gold rounded-full" animate={{ width: `${progress.percent}%` }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} data-testid="learn-progress-bar" />
            </div>
          </div>
          {cert && (
            <div className="mt-8 rounded-sm border border-gold/50 bg-gold/10 p-5 flex flex-wrap items-center gap-4 max-w-xl" data-testid="learn-cert-banner">
              <Award size={24} className="text-gold shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">Formation certifiée</p>
                <p className="text-paper/60 text-sm">Certificat n° {cert.certificate_number} — score {cert.qcm_score} %</p>
              </div>
              <a href={`${API}/certificates/${cert.certificate_number}/pdf`} data-testid="learn-cert-download"
                className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-5 py-2.5 label-mono hover:bg-paper transition-colors">PDF</a>
            </div>
          )}
          {!cert && progress.percent >= 100 && (
            <Link to={`/formation/${lookup}/qcm`} data-testid="learn-goto-qcm"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold text-ink px-7 py-4 label-mono hover:bg-paper transition-colors">
              <Award size={16} /> Passer le QCM final
            </Link>
          )}
        </div>
      </section>

      <section className="bg-paper py-16 md:py-20" data-testid="learn-modules">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <span className="label-mono text-gold">[ Contenu de la formation ]</span>
          {modules.length === 0 ? (
            <div className="rounded-sm border hairline bg-white p-10 mt-6 text-center text-ink/55">
              Les modules de cette formation seront publiés prochainement par NovaLean Consulting.
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              {modules.map((m, i) => {
                const done = progress.completed_modules?.includes(m.id);
                const Icon = MOD_ICON[m.file_ext] || FileText;
                return (
                  <motion.div key={m.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className={`rounded-sm border bg-white p-5 flex items-center gap-4 transition-colors ${done ? "border-green-200" : "hairline"}`} data-testid={`learn-module-${m.id}`}>
                    <span className="w-11 h-11 rounded-sm bg-ink-900 text-gold flex items-center justify-center shrink-0"><Icon size={18} /></span>
                    <div className="flex-1 min-w-0">
                      <p className="label-mono text-ink/40">Module {i + 1} · {String(m.file_ext || "").toUpperCase()}</p>
                      <h3 className="font-display text-lg font-semibold leading-tight truncate">{m.title}</h3>
                      {m.description && <p className="text-ink/55 text-sm mt-1 line-clamp-1">{m.description}</p>}
                    </div>
                    <button onClick={() => downloadModule(m)} data-testid={`learn-download-${m.id}`} aria-label="Télécharger"
                      className="w-10 h-10 rounded-full border hairline flex items-center justify-center text-ink/50 hover:text-gold hover:border-gold transition-colors shrink-0">
                      <Download size={16} />
                    </button>
                    {done ? (
                      <span className="inline-flex items-center gap-1.5 label-mono text-green-600 shrink-0" data-testid={`learn-done-${m.id}`}><CheckCircle2 size={16} /> Terminé</span>
                    ) : (
                      <button onClick={() => complete(m)} disabled={marking === m.id} data-testid={`learn-complete-${m.id}`}
                        className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 label-mono text-ink/70 hover:border-gold hover:text-gold transition-colors shrink-0 disabled:opacity-50">
                        {marking === m.id ? <Loader2 size={13} className="animate-spin" /> : <><Circle size={13} /> Marquer terminé</>}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
          <p className="text-ink/45 text-sm mt-6">Votre progression est enregistrée automatiquement côté serveur. Le QCM final se débloque à 100 %.</p>
        </div>
      </section>
    </>
  );
}
